import { useTheme } from '@mui/material';
import MuiTooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import React, { ReactNode, useState } from 'react';

export type PopupComponentType =
  | ((isTooltipOpen: boolean, closeTooltip: () => void) => ReactNode)
  | ReactNode;

type TooltipPopupProps = {
  children: React.ReactElement;
  popupComponent: PopupComponentType;
  placement?: 'bottom' | 'left' | 'right' | 'top';
  colorScheme?: 'extraDark' | 'dark' | 'light';
  margin?: string;
  arrow?: boolean;
  border?: boolean;
  enabled?: boolean;
  customBgColor?: string;
};

/**
 * @deprecated Use the Tooltip component in sprouts instead.
 */
const TooltipPopup = ({
  children,
  popupComponent,
  placement = 'bottom',
  colorScheme = 'dark',
  margin = '0',
  arrow = true,
  border = false,
  customBgColor,
  enabled = true,
}: TooltipPopupProps) => {
  const theme = useTheme();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  if (!children || !popupComponent) {
    return null;
  }

  if (!enabled) {
    return children;
  }

  const getBackgroundColor = () => {
    if (customBgColor) {
      return customBgColor;
    }

    switch (colorScheme) {
      case 'extraDark':
        return theme.colors.darkTheme.grey.dark;
      case 'dark':
        return theme.colors.darkTheme.grey.medium;
      case 'light':
        return theme.colors.common.softWhite;
      default:
        return theme.colors.darkTheme.grey.medium;
    }
  };

  const bgColor = getBackgroundColor();

  const onTooltipOpen = () => {
    setIsTooltipOpen(true);
  };

  const onTooltipClose = () => {
    setIsTooltipOpen(false);
  };

  // Check if popupComponent is a function, and if so, call it with isTooltipOpen and onTooltipClose.
  const resolvedPopupComponent =
    typeof popupComponent === 'function'
      ? (
          popupComponent as (
            isTooltipOpen: boolean,
            closeTooltip: () => void
          ) => ReactNode
        )(isTooltipOpen, onTooltipClose)
      : popupComponent;

  return (
    <MuiTooltip
      disableFocusListener
      disableTouchListener
      TransitionComponent={Zoom}
      title={resolvedPopupComponent}
      placement={placement}
      arrow={arrow}
      onOpen={onTooltipOpen}
      onClose={onTooltipClose}
      open={isTooltipOpen}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: '100%',
            margin: margin,
            backgroundColor: bgColor,
            borderRadius: theme.borderRadius.primary,
            border: border
              ? `1px solid ${theme.colors.darkTheme.grey.muted}`
              : 'none',
          },
        },
        arrow: {
          sx: {
            color: bgColor,
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
};

export default TooltipPopup;
